import { Component, Input, ChangeDetectionStrategy, OnChanges, SimpleChanges, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SecurityContext } from '@angular/core';

// PrimeNG
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePicker } from 'primeng/datepicker';
import { RadioButton } from 'primeng/radiobutton';
import { Checkbox } from 'primeng/checkbox';
import { Select } from 'primeng/select';

// Local
import { Field, FieldType } from '../../form-designer/models/field.model';
import { CountryList } from '../../../../api/enum/country-list';
import { DatePickerComponent } from '../../date-picker/date-picker.component';
import { CustomSingleChoiceComponent } from './custom-single-choice/custom-single-choice.component';
import { CustomYesNoComponent } from './custom-yes-no/custom-yes-no.component';

@Component({
  selector: 'app-field-renderer',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    TextareaModule,
    InputNumberModule,
    DatePicker,
    RadioButton,
    Checkbox,
    Select,
    DatePickerComponent,
    CustomSingleChoiceComponent,
    CustomYesNoComponent,
  ],
  templateUrl: './field-renderer.component.html',
  styleUrls: ['./field-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FieldRendererComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) field!: Field;
  @Input({ required: true }) fieldKey!: string;
  @Input({ required: true }) parentFormGroup!: FormGroup;
  @Input() readonly: boolean = false;
  @Input() fieldEnabled: boolean = true;
  @Input() index: number | null = null; // For repeatable fields, the index of the instance

  // Pre-computed values to avoid repeated calculations in template
  fieldOptions: { label: string; value: string }[] = [];
  effectiveFieldType: string = '';
  fieldLayoutClass: string = '';
  maxLengthText: number | null = null;
  maxLengthTextarea: number | null = null;
  countryOptions: { label: string; value: string }[] = [];
  isInvalid: boolean = false;
  fieldError: string | null = null;
  isDisabled: boolean = false;

  // Cached FormControl reference
  private _formControl: FormControl | null = null;

  // Subscriptions for form control state changes
  private _statusChangesSubscription?: Subscription;
  private _valueChangesSubscription?: Subscription;

  // Static cache for country options to avoid repeated sorting and mapping
  private static _countryOptionsCache: { label: string; value: string }[] | null = null;

  constructor(private cdr: ChangeDetectorRef, private sanitizer: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['field'] || changes['fieldKey'] || changes['parentFormGroup']) {
      this.initializeComponent();
    }

    // Handle fieldEnabled changes to update form control state
    if (changes['fieldEnabled'] && !changes['fieldEnabled'].firstChange) {
      this.updateFieldDisabledState();
    }
  }

  ngOnDestroy() {
    this.unsubscribeFromFormControl();
  }

  private initializeComponent() {
    this.unsubscribeFromFormControl(); // Clean up previous subscriptions
    this._formControl = null; // Reset cached FormControl
    this.precomputeValues();
    this.subscribeToFormControlChanges();
    // Ensure FormControl disabled state is synchronized after initialization
    setTimeout(() => {
      this.updateFieldDisabledState();
    }, 0);
  }

  private subscribeToFormControlChanges() {
    // Use setTimeout to ensure FormControl is available after component initialization
    setTimeout(() => {
      const control = this.formControl;

      if (control) {
        // Subscribe to status changes (valid/invalid, touched/untouched, dirty/pristine)
        this._statusChangesSubscription = control.statusChanges.subscribe(() => {
          this.updateValidationState();
          this.cdr.markForCheck(); // Trigger change detection
        });

        // Subscribe to value changes to update validation state
        this._valueChangesSubscription = control.valueChanges.subscribe(() => {
          this.updateValidationState();
          this.cdr.markForCheck(); // Trigger change detection
        });
      }
    }, 0);
  }

  private unsubscribeFromFormControl() {
    if (this._statusChangesSubscription) {
      this._statusChangesSubscription.unsubscribe();
      this._statusChangesSubscription = undefined;
    }
    if (this._valueChangesSubscription) {
      this._valueChangesSubscription.unsubscribe();
      this._valueChangesSubscription = undefined;
    }
  }

  private precomputeValues() {
    // Pre-compute effective field type and field options
    this.effectiveFieldType = this.computeEffectiveFieldType();

    // Pre-compute field layout class
    this.fieldLayoutClass = this.computeFieldLayoutClass();

    // Pre-compute max lengths
    this.maxLengthText = this.computeMaxLength('text');
    this.maxLengthTextarea = this.computeMaxLength('textarea');

    // Pre-compute country options (uses static cache)
    this.countryOptions = this.getCountryOptions();

    // Pre-compute disabled state for template binding
    this.isDisabled = this.isFieldDisabled;

    // Pre-compute validation state and error message
    this.updateValidationState();
  }

  private updateValidationState() {
    const control = this.formControl;

    this.isInvalid = !!(control && control.invalid && (control.dirty || control.touched));
    this.fieldError = this.computeFieldError();
  }

  /**
   * Update the form control's disabled state based on field enabled status
   */
  private updateFieldDisabledState(): void {
    const control = this.formControl;
    if (!control) return;

    const shouldBeDisabled = this.isFieldDisabled;

    // Update FormControl state
    if (shouldBeDisabled && control.enabled) {
      control.disable({ emitEvent: false });
    } else if (!shouldBeDisabled && control.disabled) {
      control.enable({ emitEvent: false });
    }

    // Update precomputed value for template binding
    this.isDisabled = shouldBeDisabled;

    this.cdr.markForCheck();
  }

  /**
   * Determine if the field should be disabled
   */
  get isFieldDisabled(): boolean {
    return this.readonly || this.field.readOnly || !this.fieldEnabled;
  }

  private computeFieldError(): string | null {
    const control = this.formControl;
    if (control && control.errors && (control.dirty || control.touched)) {
      if (control.errors['required']) {
        return 'This field is required';
      }
      if (control.errors['minlength']) {
        return `Minimum length is ${control.errors['minlength'].requiredLength}`;
      }
      if (control.errors['maxlength']) {
        return `Maximum length is ${control.errors['maxlength'].requiredLength}`;
      }
      if (control.errors['min']) {
        return `Minimum value is ${control.errors['min'].min}`;
      }
      if (control.errors['max']) {
        return `Maximum value is ${control.errors['max'].max}`;
      }
      if (control.errors['pattern']) {
        return 'Invalid format';
      }
    }
    return null;
  }

  private computeEffectiveFieldType(): string {
    if (this.field.type === FieldType.KEYWORD_FIELD) {
      const renderType = this.field['renderType'];

      if (renderType === FieldType.DROPDOWN || renderType === FieldType.SINGLE_CHOICE || renderType === FieldType.MULTIPLE_CHOICE) {
        this.fieldOptions = this.getOptions();

        // Special handling for DROPDOWN: check choiceDisplayType
        if (renderType === FieldType.DROPDOWN) {
          const choiceDisplayType = this.field['choiceDisplayType'] || 'dropdown';
          return choiceDisplayType === 'options' ? FieldType.SINGLE_CHOICE : FieldType.DROPDOWN;
        }
      }

      return renderType || 'LABEL';
    }

    if (this.field.type === FieldType.DROPDOWN || this.field.type === FieldType.SINGLE_CHOICE || this.field.type === FieldType.MULTIPLE_CHOICE) {
      this.fieldOptions = this.getOptions();
    }

    return this.field.type;
  }

  private computeFieldLayoutClass(): string {
    const layout = this.field['layout'] || 'horizontal';
    return layout === 'vertical' ? ' field-radio-group-vertical' : '';
  }

  private computeMaxLength(fieldType: string): number | null {
    if (fieldType === FieldType.TEXT) {
      return this.field['maxLength'] ?? 255;
    } else if (fieldType === FieldType.TEXTAREA) {
      return this.field['maxLength'] || null;
    }
    return null;
  }

  getOptions(): { label: string; value: string }[] {
    const options = this.field['options'];

    if (!options || options.length === 0) {
      return [];
    }

    // Handle keyword field options format: { key: string; value: string }[]
    if (options[0] && typeof options[0] === 'object' && 'key' in options[0] && 'value' in options[0]) {
      return options.map((opt: { key: string; value: string }) => ({ label: opt.value, value: opt.key }));
    }

    // Handle simple string array format
    return options.map((opt: string) => ({ label: opt, value: opt }));
  }

  getCountryOptions(): { label: string; value: string }[] {
    // Use static cache to avoid repeated sorting and mapping operations
    if (FieldRendererComponent._countryOptionsCache === null) {
      FieldRendererComponent._countryOptionsCache = CountryList
        .sort((a, b) => a.localeCompare(b))
        .map(country => ({
          label: country,
          value: country
        }));
    }
    return FieldRendererComponent._countryOptionsCache;
  }

  get formControl(): FormControl | null {
    if (!this._formControl) {
      this._formControl = this.parentFormGroup.get(this.fieldKey) as FormControl;
    }
    return this._formControl;
  }

  /**
   * Generate unique radio button name to prevent conflicts in repeatable instances
   */
  get uniqueRadioName(): string {
    return this.index !== null ? `${this.fieldKey}_${this.index}` : this.fieldKey;
  }


  // TrackBy functions for performance optimization
  trackByOptionIndex(index: number, item: any): number {
    return index;
  }

  trackByOptionValue(index: number, item: any): any {
    return item.value || item;
  }

  /**
   * Check if current field is an instruction field
   */
  isInstructionField(): boolean {
    return this.field.type === FieldType.INSTRUCTION;
  }

  /**
   * Get safe HTML for instruction field content
   */
  getSafeInstructionContent(): SafeHtml {
    const content = this.field['content'] || '<p>Instruction content will appear here...</p>';
    // Use sanitize to remove dangerous content while keeping safe HTML
    return this.sanitizer.sanitize(SecurityContext.HTML, content) || '';
  }

  /**
   * Get safe HTML for help text content
   */
  getSafeHelpTextContent(): SafeHtml {
    const helpText = this.field['helpText'] || '';
    // Use sanitize to remove dangerous content while keeping safe HTML
    return this.sanitizer.sanitize(SecurityContext.HTML, helpText) || '';
  }

  // Public method for manual validation state refresh
  public refreshValidationState(): void {
    this.updateValidationState();
    this.cdr.markForCheck();
  }
}
